import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chats } from './Chats/Chats';
import ChatLauncher from './ChatLauncher';
import { ChatGlyph, ChevronGlyph } from './chatIcons';
import classes from './ChatBox.module.css';

/** Live comments for everyone on the map: a panel above a round launcher, bottom-right. */
export const ChatBox = ({ id }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    // The socket listener inside <Chats> is registered once, so it reads
    // "is the panel open?" from this ref rather than from mount-time state.
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const notifyIncomingMessage = useCallback(() => {
        if (!isOpenRef.current) setUnreadCount((count) => count + 1);
    }, []);

    const openChat = () => {
        setIsOpen(true);
        setUnreadCount(0);
    };

    const closeChat = useCallback(() => setIsOpen(false), []);

    // Escape closes the panel, like the app's other overlays.
    useEffect(() => {
        if (!isOpen) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') closeChat();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeChat]);

    return (
        <div className={classes.root}>
            <section
                className={`${classes.panel} ${isOpen ? classes.panelOpen : ''}`.trim()}
                role="dialog"
                aria-label="Mind map comments"
                aria-hidden={!isOpen}
            >
                <header className={classes.header}>
                    <span className={classes.headerIcon} aria-hidden="true">
                        <ChatGlyph />
                    </span>
                    <div className={classes.headerText}>
                        <span className={classes.headerTitle}>Comments</span>
                        <span className={classes.headerSubtitle}>
                            <i className={classes.liveDot} aria-hidden="true" />
                            Live with everyone here
                        </span>
                    </div>
                    <button
                        type="button"
                        className={classes.headerClose}
                        onClick={closeChat}
                        aria-label="Close comments"
                        title="Close comments"
                    >
                        <ChevronGlyph />
                    </button>
                </header>

                <Chats id={id} isOpen={isOpen} onIncomingMessage={notifyIncomingMessage} />
            </section>

            <ChatLauncher isOpen={isOpen} unreadCount={unreadCount} onToggle={isOpen ? closeChat : openChat} />
        </div>
    );
};
